import { animate } from 'animejs';
import React, { useEffect, useRef } from 'react';

interface AnimatedTextProps {
  text: string;
  className?: string;
  delay: number;
}

export const AnimatedText: React.FC<AnimatedTextProps> = ({
  text,
  delay = 0,
  className = '',
}) => {
  const root = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const animationRef = useRef<any>(null);
  const hasAnimatedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!root.current || !textRef.current) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting && textRef.current) {
          if (animationRef.current) {
            animationRef.current.reset();
          }

          animationRef.current = animate(textRef.current, {
            opacity: [0, 1],
            scale: [0.3, 1],
            duration: 500,
            delay: delay,
            easing: 'easeOutElastic(1, .3)',
          });

          hasAnimatedRef.current = true;
        } else if (hasAnimatedRef.current && textRef.current) {
          if (animationRef.current) {
            animationRef.current.reset();
          }

          animationRef.current = animate(textRef.current, {
            opacity: [1, 0],
            scale: [1, 0.3],
            duration: 500,
            delay: 0,
            easing: 'easeInElastic(1, .3)',
          });
        }
      },
      {
        threshold: 0.5,
      }
    );

    observer.observe(root.current);

    return () => {
      observer.disconnect();
      if (animationRef.current) {
        animationRef.current.reset();
      }
    };
  }, [text, delay]);

  return (
    <div ref={root} className={className}>
      <span
        ref={textRef}
        style={{ opacity: 0, transform: 'scale(0.3)', display: 'inline-block' }}
      >
        {text}
      </span>
    </div>
  );
};
